import { useCrmLocale } from '../ui/locale/crmLocale';
import React from 'react';
import {
  UNSAFE_DataRouterContext,
  useBeforeUnload,
  useBlocker,
} from 'react-router-dom';

import { useConfirm } from '../ui/confirm/useConfirm';
import { NAVIGATION_PURGE_EVENT } from './sessionStore';

export interface DirtyGuardOptions {
  when: boolean;
  message?: string;
  onDiscard?: () => void;
  /** Opt-in: in-flight mutations must stay visible and cannot be discarded. */
  preventDiscard?: boolean;
  onStay?: () => void;
  onBlocked?: () => void;
}

interface DirtyGuardRegistry {
  register(id: string, options: DirtyGuardOptions): () => void;
  update(id: string, options: DirtyGuardOptions): void;
}

interface OwnedDirtyPrompt {
  attemptId: string;
  dismiss(): void;
  dismissSilently(): void;
  proceedWhenSafe(): void;
}

const DirtyNavigationContext = React.createContext<DirtyGuardRegistry | null>(null);

function blockedAttemptId(location: {
  key: string;
  pathname: string;
  search: string;
  hash: string;
}): string {
  return JSON.stringify([
    location.key,
    location.pathname,
    location.search,
    location.hash,
  ]);
}

function DirtyNavigationBlockerHost({
  getActiveOwners,
  registryRevision,
}: {
  getActiveOwners: () => DirtyGuardOptions[];
  registryRevision: number;
}) {
  const { t: crmText } = useCrmLocale();

  const shouldBlock = React.useCallback(
    () => getActiveOwners().length > 0,
    [getActiveOwners],
  );
  const blocker = useBlocker(shouldBlock);
  const { confirm } = useConfirm();
  const ownedPromptRef = React.useRef<OwnedDirtyPrompt | null>(null);

  React.useEffect(() => {
    const activeOwners = getActiveOwners();
    if (blocker.state === 'blocked' && activeOwners.some(owner => owner.preventDiscard)) {
      const owned = ownedPromptRef.current;
      ownedPromptRef.current = null;
      owned?.dismissSilently();
      blocker.reset();
      for (const owner of activeOwners) owner.onBlocked?.();
      return;
    }
    if (blocker.state === 'blocked' && activeOwners.length === 0) {
      const owned = ownedPromptRef.current;
      ownedPromptRef.current = null;
      if (owned) {
        owned.proceedWhenSafe();
      } else {
        try {
          blocker.proceed();
        } catch {
          // The blocker may already be resolving during an owner teardown.
        }
      }
      return;
    }

    if (blocker.state !== 'blocked') {
      const owned = ownedPromptRef.current;
      ownedPromptRef.current = null;
      owned?.dismissSilently();
      return;
    }

    const attemptId = blockedAttemptId(blocker.location);
    const existingPrompt = ownedPromptRef.current;
    if (existingPrompt?.attemptId === attemptId) return;
    if (existingPrompt) {
      ownedPromptRef.current = null;
      existingPrompt.dismiss();
      try {
        blocker.reset();
      } catch {
        // The first prompt may already have reset this superseding attempt.
      }
      return;
    }

    let settled = false;
    const reset = () => {
      try {
        blocker.reset();
      } catch {
        // A route may unmount while the provider is dismissing the prompt.
      }
    };
    const clearOwnedPrompt = () => {
      if (ownedPromptRef.current?.attemptId === attemptId) {
        ownedPromptRef.current = null;
      }
    };
    let confirmation: ReturnType<typeof confirm> | null = null;
    const dismissSilently = () => {
      if (settled) return;
      settled = true;
      clearOwnedPrompt();
      confirmation?.();
    };
    const proceedWhenSafe = () => {
      if (settled) return;
      settled = true;
      clearOwnedPrompt();
      confirmation?.();
      try {
        blocker.proceed();
      } catch {
        // The owner may have unmounted while the router was resolving.
      }
    };
    confirmation = confirm(
      activeOwners.find((owner) => owner.message)?.message
        ?? crmText("You have unsaved changes. Leave this page?"),
      () => {
        if (settled) return;
        settled = true;
        clearOwnedPrompt();
        const owners = getActiveOwners();
        if (owners.some(owner => owner.preventDiscard)) {
          reset();
          for (const owner of owners) owner.onBlocked?.();
          return;
        }
        try {
          for (const owner of owners) owner.onDiscard?.();
        } catch {
          reset();
          return;
        }
        try {
          blocker.proceed();
        } catch {
          reset();
        }
      },
      'Unsaved changes',
      () => {
        if (settled) return;
        settled = true;
        clearOwnedPrompt();
        reset();
        for (const owner of getActiveOwners()) owner.onStay?.();
      },
    );
    if (!confirmation.accepted) {
      reset();
      return;
    }
    ownedPromptRef.current = {
      attemptId,
      dismiss: confirmation,
      dismissSilently,
      proceedWhenSafe,
    };
  }, [blocker, confirm, getActiveOwners, registryRevision, crmText]);

  React.useEffect(() => {
    const dismissOwnedPrompt = () => {
      const owned = ownedPromptRef.current;
      ownedPromptRef.current = null;
      owned?.dismiss();
    };
    window.addEventListener(NAVIGATION_PURGE_EVENT, dismissOwnedPrompt);
    return () => {
      window.removeEventListener(NAVIGATION_PURGE_EVENT, dismissOwnedPrompt);
      const owned = ownedPromptRef.current;
      ownedPromptRef.current = null;
      owned?.dismissSilently();
    };
  }, []);

  useBeforeUnload(React.useCallback((event: BeforeUnloadEvent) => {
    if (getActiveOwners().length === 0) return;
    event.preventDefault();
    event.returnValue = '';
  }, [getActiveOwners]));

  return null;
}

/**
 * Own the router's single blocker and aggregate every dirty draft below it.
 * React Router supports one blocker per router, while a page can legitimately
 * contain several independently persisted forms.
 */
export function DirtyNavigationProvider({ children }: { children: React.ReactNode }) {
  const dataRouter = React.useContext(UNSAFE_DataRouterContext);
  const ownersRef = React.useRef(new Map<string, DirtyGuardOptions>());
  const [registryRevision, bumpRegistryRevision] = React.useReducer((value: number) => value + 1, 0);

  const register = React.useCallback((id: string, options: DirtyGuardOptions) => {
    ownersRef.current.set(id, options);
    bumpRegistryRevision();
    return () => {
      if (ownersRef.current.delete(id)) bumpRegistryRevision();
    };
  }, []);

  const update = React.useCallback((id: string, options: DirtyGuardOptions) => {
    const previous = ownersRef.current.get(id);
    ownersRef.current.set(id, options);
    if (!previous || previous.when !== options.when
      || previous.preventDiscard !== options.preventDiscard) bumpRegistryRevision();
  }, []);

  const registry = React.useMemo<DirtyGuardRegistry>(() => ({ register, update }), [register, update]);
  const getActiveOwners = React.useCallback(
    () => [...ownersRef.current.values()].filter((owner) => owner.when),
    [],
  );
  return React.createElement(
    DirtyNavigationContext.Provider,
    { value: registry },
    dataRouter
      ? React.createElement(DirtyNavigationBlockerHost, { getActiveOwners, registryRevision })
      : null,
    children,
  );
}

export function useDirtyNavigationGuard(options: DirtyGuardOptions, optional = false): () => void {
  const registry = React.useContext(DirtyNavigationContext);
  const id = React.useId();
  const { when, message, onDiscard, preventDiscard, onStay, onBlocked } = options;
  const initialOptionsRef = React.useRef<DirtyGuardOptions>(options);
  const currentOptionsRef = React.useRef<DirtyGuardOptions>(options);
  currentOptionsRef.current = options;
  if (!registry && !optional) {
    throw new Error('useDirtyNavigationGuard must be used within DirtyNavigationProvider');
  }

  React.useLayoutEffect(() => registry?.register(id, initialOptionsRef.current), [id, registry]);
  React.useLayoutEffect(() => {
    registry?.update(id, { when, message, onDiscard, preventDiscard, onStay, onBlocked });
  }, [id, message, onDiscard, preventDiscard, onStay, onBlocked, registry, when]);

  return React.useCallback(() => {
    registry?.update(id, { ...currentOptionsRef.current, when: false });
  }, [id, registry]);
}
